/**
 * ZEN ERP — Dashboard Sağ Panel
 * DashboardPage.jsx'ten çıkarıldı.
 * İçerir: AppleAnalogClock, CrmFollowupBox (internal), DashboardRightPanel (export)
 */
import React, { useState, useEffect } from "react";
import {
  ChevronLeft, Search, X, AlertTriangle, CalendarDays, Plus, Check, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { fmt, ono } from "../../appUtils";

// ── Apple Analog Saat ──
function AppleAnalogClock({ date, size = 132 }) {
  const h = date.getHours() % 12;
  const m = date.getMinutes();
  const s = date.getSeconds();
  const secDeg = s * 6;
  const minDeg = m * 6 + s * 0.1;
  const hourDeg = h * 30 + m * 0.5;

  const ticks = [];
  for (let i = 0; i < 60; i++) {
    const isHour = i % 5 === 0;
    ticks.push(
      <rect
        key={i}
        x={isHour ? 49.1 : 49.6}
        y={isHour ? 6 : 6.6}
        width={isHour ? 1.8 : 0.8}
        height={isHour ? 7.5 : 3.4}
        rx={isHour ? 0.9 : 0.4}
        fill={isHour ? "#1C1C1E" : "#B9BCC2"}
        transform={`rotate(${i * 6} 50 50)`}
      />
    );
  }

  return (
    <div className="zen-analog-clock" style={{ width: size, height: size }} data-testid="dashboard-analog-clock">
      <svg viewBox="0 0 100 100" width={size} height={size}>
        <defs>
          <radialGradient id="zenClockFace" cx="50%" cy="34%" r="70%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="72%" stopColor="#FBFBFD" />
            <stop offset="100%" stopColor="#EDEEF2" />
          </radialGradient>
          <linearGradient id="zenClockBezel" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F3F4F7" />
            <stop offset="50%" stopColor="#BFC3CB" />
            <stop offset="100%" stopColor="#8B9099" />
          </linearGradient>
        </defs>
        <circle cx="50" cy="50" r="49" fill="url(#zenClockBezel)" />
        <circle cx="50" cy="50" r="46" fill="url(#zenClockFace)" />
        {ticks}
        <rect x="48.4" y="26" width="3.2" height="26" rx="1.6" fill="#1C1C1E" transform={`rotate(${hourDeg} 50 50)`} />
        <rect x="48.8" y="15" width="2.4" height="37" rx="1.2" fill="#1C1C1E" transform={`rotate(${minDeg} 50 50)`} />
        <g transform={`rotate(${secDeg} 50 50)`}>
          <rect x="49.45" y="12" width="1.1" height="43" rx="0.55" fill="#FF9500" />
          <circle cx="50" cy="50" r="3.1" fill="#FF9500" />
          <circle cx="50" cy="50" r="1.1" fill="#FFFFFF" />
        </g>
      </svg>
    </div>
  );
}

// ── CRM Takip Kutusu ──
function CrmFollowupBox({ tr, setTab }) {
  const [data, setData] = useState({ overdue: [], today: [] });
  useEffect(() => {
    api.get("/crm/followups").then(r => setData(r.data || { overdue: [], today: [] })).catch(() => {});
  }, []);
  const total = (data.overdue?.length || 0) + (data.today?.length || 0);
  if (total === 0) return null;
  return (
    <div className="zen-panel p-4" data-testid="crm-followup-box">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
        {tr ? tr("CRM Takip") : "CRM Takip"}
      </div>
      {data.overdue?.length > 0 && (
        <div className="text-[12px] text-rose-500 font-semibold">{data.overdue.length} gecikmiş takip</div>
      )}
      {data.today?.length > 0 && (
        <div className="text-[12px] text-amber-500 font-semibold">{data.today.length} bugünkü takip</div>
      )}
      <button onClick={() => setTab && setTab("crm")} className="mt-2 text-[11px] text-blue-500 hover:underline">
        {tr ? tr("CRM'e git") : "CRM'e git"}
      </button>
    </div>
  );
}

// ── Dashboard Sağ Panel ──
export function DashboardRightPanel({
  summary,
  rates,
  ratesError,
  tr,
  lang,
  dateLocale,
  clockStyle,
  now,
  setTab,
  setViewingOrder,
  user,
  manualTermins,
  setShowAlarmModal,
  setShowTerminModal,
  searchQ,
  setSearchQ,
  searchResults,
  searchLoading,
  onApproveOrder,
  onRefreshRates,
}) {
  const [rightOpen, setRightOpen] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefreshRates = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/rates/refresh-tcmb");
      if (data?.ok) {
        toast.success(`Kur güncellendi — EUR: ₺${Number(data.eurToTl).toFixed(4)} · USD: ₺${Number(data.usdToTl).toFixed(4)}`);
        if (onRefreshRates) onRefreshRates();
      } else {
        toast.error(`TCMB hatası: ${data?.error || "bilinmeyen hata"}`);
      }
    } catch (e) {
      toast.error("Kur güncellenemedi");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <aside className="relative shrink-0 xl:mt-0 mt-6 xl:sticky xl:top-0 xl:self-start flex flex-col xl:flex-row">
      {/* Collapse tab — dar ekranda widget'ların üstünde görünür, xl'de kenar tutamağı */}
      <button
        onClick={() => setRightOpen(v => !v)}
        data-testid="right-panel-toggle"
        title={rightOpen ? tr("Paneli kapat") : tr("Paneli aç")}
        className="flex items-center justify-center self-end mx-1 mt-1 h-5 w-9 rounded-lg shrink-0
                   xl:self-start xl:mx-0 xl:mt-20 xl:h-12 xl:w-5 xl:rounded-l-lg xl:border-r-0
                   bg-slate-100 dark:bg-[#1A2340] hover:bg-slate-200 dark:hover:bg-[#22304A]
                   border border-slate-200 dark:border-[#22304A] transition-colors cursor-pointer"
      >
        <ChevronLeft className={`w-3.5 h-3.5 text-slate-400 transition-transform duration-300 ${rightOpen ? "rotate-180" : ""}`} />
      </button>

      <div
        className="overflow-hidden transition-all duration-300"
        style={{ width: rightOpen ? "" : 0, minWidth: rightOpen ? "" : 0 }}
      >
        {/* Widget ızgarası: dar ekranda yatay (üstteki menülerle hizalı), xl'de dikey kenar çubuğu */}
        <div className="xl:w-72 2xl:w-80 w-full grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 px-3 xl:px-0 xl:grid-cols-1 xl:gap-3">

          {/* 1. Saat / Tarih / Kur */}
          <div className="zen-panel p-4 overflow-hidden relative">
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-indigo-500/8 blur-2xl pointer-events-none" />
            {clockStyle === "analog" ? (
              <div className="flex flex-col items-center" data-testid="dashboard-clock-analog-wrap">
                <button onClick={() => setShowAlarmModal(true)} data-testid="clock-alarm-btn" className="hover:opacity-80 transition-opacity cursor-pointer focus:outline-none" title="Alarmlar & Hatırlatıcılar">
                  <AppleAnalogClock date={now} />
                </button>
                <div className="zen-clock-digital text-[13px] font-semibold text-slate-700 dark:text-slate-200 mt-2.5 tabular-nums">
                  {now.toLocaleTimeString(dateLocale, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 font-medium text-center">
                  {now.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
              </div>
            ) : (
              <button onClick={() => setShowAlarmModal(true)} data-testid="clock-alarm-btn-digital" className="text-left w-full hover:opacity-80 transition-opacity cursor-pointer focus:outline-none" title="Alarmlar & Hatırlatıcılar">
                <div className="zen-clock-digital text-3xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 leading-none tabular-nums" data-testid="dashboard-clock-digital">
                  {now.toLocaleTimeString(dateLocale, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 font-medium">
                  {now.toLocaleDateString(dateLocale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                </div>
              </button>
            )}
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-[#22304A] space-y-2" data-testid="dashboard-rates-widget">
              {/* EUR */}
              <div className="rounded-lg bg-slate-50 dark:bg-[#0F1626] border border-slate-200 dark:border-[#22304A] px-3 py-2">
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1.5">€ EUR / TL</div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div>
                    <div className="text-[9px] text-emerald-500 font-semibold uppercase mb-0.5">ALIŞ</div>
                    <div className="zen-mono text-[13px] font-black text-emerald-600 dark:text-emerald-400">₺{Number(rates.eurBuy || rates.eurToTl || 0).toFixed(4)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] text-[#E5322A] font-semibold uppercase mb-0.5">SATIŞ</div>
                    <div className="zen-mono text-[13px] font-black text-[#E5322A]">₺{Number(rates.eurSell || rates.eurToTl || 0).toFixed(4)}</div>
                  </div>
                </div>
              </div>
              {/* USD */}
              <div className="rounded-lg bg-slate-50 dark:bg-[#0F1626] border border-slate-200 dark:border-[#22304A] px-3 py-2">
                <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-1.5">$ USD / TL</div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div>
                    <div className="text-[9px] text-emerald-500 font-semibold uppercase mb-0.5">ALIŞ</div>
                    <div className="zen-mono text-[13px] font-black text-emerald-600 dark:text-emerald-400">₺{Number(rates.usdBuy || rates.usdToTl || 0).toFixed(4)}</div>
                  </div>
                  <div>
                    <div className="text-[9px] text-sky-500 font-semibold uppercase mb-0.5">SATIŞ</div>
                    <div className="zen-mono text-[13px] font-black text-sky-600 dark:text-sky-400">₺{Number(rates.usdSell || rates.usdToTl || 0).toFixed(4)}</div>
                  </div>
                </div>
              </div>
            </div>
            {rates.tcmbDate && rates.tcmbDate !== "-" && (
              <div className="text-[10px] text-white dark:text-white mt-1.5 text-right">TCMB+0.50 · {(() => { try { const raw = rates.tcmbDate; if (raw.includes("/")) { const [mm,dd,yyyy] = raw.split("/"); return new Date(`${yyyy}-${mm}-${dd}`).toLocaleDateString("tr-TR",{day:"numeric",month:"long",year:"numeric"}); } const [d,m,y] = raw.split("."); return new Date(`${y}-${m}-${d}`).toLocaleDateString("tr-TR",{day:"numeric",month:"long",year:"numeric"}); } catch(e) { return rates.tcmbDate; } })()}</div>
            )}
            <button
              onClick={handleRefreshRates}
              disabled={refreshing}
              data-testid="tcmb-refresh-btn"
              className="mt-2 w-full flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-[11px] font-semibold border border-slate-300 dark:border-[#22304A] text-slate-600 dark:text-slate-300 hover:border-[#E5322A]/60 hover:text-[#E5322A] dark:hover:text-[#E5322A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white dark:bg-[#0F1626]"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Güncelleniyor..." : "TCMB'den Güncelle"}
            </button>
            {ratesError && (
              <div className="flex items-center gap-1 text-[10px] text-amber-400 mt-1">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                <span>Kur kaynağına ulaşılamadı — son kayıtlı kur kullanılıyor</span>
              </div>
            )}
          </div>

          {/* 2. Onay Bekliyor Widget */}
          {(summary.onayBekliyor || []).length > 0 && (
            <div className="zen-panel p-4 !border-orange-500/50" data-testid="onay-bekliyor-widget">
              <div className="text-[11px] font-bold uppercase tracking-wider text-orange-500 mb-3 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" />
                {tr("Onay Bekliyor")}
                <span className="ml-auto bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                  {(summary.onayBekliyor || []).length}
                </span>
              </div>
              <div className="space-y-2">
                {(summary.onayBekliyor || []).map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-100 dark:border-[#22304A] last:border-0">
                    <div className="min-w-0">
                      <div className="zen-mono text-[11px] font-bold text-[#E5322A]">{ono(o)}</div>
                      <div className="text-[10px] text-slate-500 truncate">{o.customerName}</div>
                    </div>
                    <button
                      data-testid={`approve-btn-${o.id}`}
                      onClick={() => onApproveOrder(o)}
                      className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md bg-orange-500/15 hover:bg-orange-500 text-orange-600 dark:text-orange-400 hover:text-white text-[10px] font-bold transition-colors"
                    >
                      <Check className="w-3 h-3" /> {tr("Onayla")}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 3. Sipariş Hızlı Arama */}
          <div className="zen-panel p-3 relative" data-testid="quick-search-widget">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-[#0F1626] border border-slate-200 dark:border-[#22304A]">
              <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <input
                data-testid="quick-search-input"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onBlur={() => setTimeout(() => setSearchQ(""), 200)}
                placeholder={tr("Sipariş ara... (no, müşteri)")}
                className="bg-transparent outline-none text-[12px] flex-1 placeholder-slate-400 dark:placeholder-slate-600 text-slate-800 dark:text-slate-100"
              />
              {searchLoading && <div className="w-3 h-3 rounded-full border-2 border-slate-400 border-t-transparent animate-spin shrink-0" />}
              {searchQ && !searchLoading && <button onClick={() => setSearchQ("")} className="text-slate-400 hover:text-slate-600"><X className="w-3 h-3" /></button>}
            </div>
            {searchResults.length > 0 && (
              <div className="absolute left-3 right-3 top-full mt-1 zen-panel !p-0 overflow-hidden z-50 shadow-xl">
                {searchResults.map((o) => {
                  const sc = { teklif: "bg-slate-500/15 text-slate-500", onay_bekliyor: "bg-orange-500/15 text-orange-600", imalata_hazir: "bg-emerald-500/15 text-emerald-700", imalatta: "bg-amber-500/15 text-amber-600", kargolanacak: "bg-sky-500/15 text-sky-600", yollandi: "bg-orange-500/15 text-orange-600", odendi: "bg-emerald-500/15 text-emerald-600" };
                  const sl = lang === "en"
                    ? { teklif: "Quote", onay_bekliyor: "Pending", imalata_hazir: "Approved", imalatta: "In Prod.", kargolanacak: "Shipping", yollandi: "Shipped", odendi: "Paid" }
                    : { teklif: "Teklif", onay_bekliyor: "Onay Bek.", imalata_hazir: "İmalata Hazır", imalatta: "İmalatta", kargolanacak: "Kargoda", yollandi: "Yollandı", odendi: "Ödendi" };
                  return (
                    <button
                      key={o.id}
                      data-testid={`search-result-${o.id}`}
                      onMouseDown={() => { setViewingOrder(o); setSearchQ(""); }}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-[#22304A] border-b border-slate-100 dark:border-[#22304A] last:border-0 transition-colors"
                    >
                      <div className="text-left min-w-0">
                        <div className="zen-mono text-[11px] font-bold text-[#E5322A]">{ono(o)}</div>
                        <div className="text-[10px] text-slate-500 truncate">{o.customerName}{o.projectName ? ` · ${o.projectName}` : ""}</div>
                      </div>
                      <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-md ${sc[o.status] || "bg-slate-500/15 text-slate-500"}`}>{sl[o.status] || o.status}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* 4. Termin Takvimi */}
          <div className="zen-panel p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5" /> {tr("Termin Takvimi")}
              <button onClick={() => setShowTerminModal(true)} data-testid="termin-add-btn"
                className="ml-auto p-0.5 rounded-md hover:bg-[#E5322A]/10 text-slate-400 hover:text-[#E5322A] transition-colors" title="Manuel Termin Ekle">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-3">
              {Array.from({ length: 7 }, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() + i);
                const dayStr = d.toISOString().slice(0, 10);
                const hits = (summary.deadlineWarnings || []).filter(o => o.deadline === dayStr);
                const manualHits = manualTermins.filter(r => r.date === dayStr);
                const totalHits = hits.length + manualHits.length;
                const isToday = i === 0;
                const dayName = d.toLocaleDateString(dateLocale, { weekday: "short" });
                return (
                  <div
                    key={dayStr}
                    title={[...hits.map(o => `${ono(o)} — ${o.customerName}`), ...manualHits.map(r => r.title)].join("\n") || dayStr}
                    className={`relative flex flex-col items-center py-1.5 rounded-lg text-center select-none
                      ${isToday ? "bg-[#E5322A]/10 border border-[#E5322A]/30" : totalHits > 0 ? "bg-amber-500/10 border border-amber-500/30" : "border border-slate-200/50 dark:border-[#22304A]"}`}
                  >
                    <span className="text-[9px] uppercase text-slate-400 dark:text-slate-500">{dayName}</span>
                    <span className={`text-[13px] font-extrabold mt-0.5 ${isToday ? "text-[#E5322A]" : totalHits > 0 ? "text-amber-600 dark:text-amber-400" : "text-slate-600 dark:text-slate-300"}`}>{d.getDate()}</span>
                    {totalHits > 0 && (
                      <span className="absolute -top-1.5 -right-1 min-w-[16px] h-4 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5 shadow">
                        {totalHits}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {((summary.deadlineWarnings || []).length > 0 || manualTermins.length > 0) ? (
              <div className="space-y-0 border-t border-slate-200 dark:border-[#22304A] pt-2">
                {(summary.deadlineWarnings || []).slice(0, 4).map((o) => {
                  const todayStr = new Date().toISOString().slice(0, 10);
                  const isLate = o.deadline < todayStr;
                  const daysLeft = Math.ceil((new Date(o.deadline) - new Date(todayStr)) / 86400000);
                  return (
                    <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-[#22304A] last:border-0">
                      <div className="min-w-0">
                        <span className="zen-mono text-[11px] font-bold text-[#E5322A] mr-1.5">{ono(o)}</span>
                        <span className="text-[10px] text-slate-500 truncate">{o.customerName}</span>
                      </div>
                      <span className={`shrink-0 ml-2 text-[10px] font-bold whitespace-nowrap ${isLate ? "text-rose-500" : daysLeft === 0 ? "text-amber-500" : "text-sky-500"}`}>
                        {isLate ? `${Math.abs(daysLeft)}${lang === "en" ? "d late" : "g gecikti"}` : daysLeft === 0 ? tr("Bugün!") : `${daysLeft}${lang === "en" ? "d" : "g"}`}
                      </span>
                    </div>
                  );
                })}
                {manualTermins.slice(0, 4).map((r) => {
                  const todayStr = new Date().toISOString().slice(0, 10);
                  const isLate = r.date < todayStr;
                  const daysLeft = Math.ceil((new Date(r.date) - new Date(todayStr)) / 86400000);
                  return (
                    <div key={r.id} className="flex items-center justify-between py-1.5 border-b border-slate-100 dark:border-[#22304A] last:border-0">
                      <div className="min-w-0 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 shrink-0" />
                        <span className="text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 truncate">{r.title}</span>
                      </div>
                      <span className={`shrink-0 ml-2 text-[10px] font-bold whitespace-nowrap ${isLate ? "text-rose-500" : daysLeft === 0 ? "text-amber-500" : "text-sky-500"}`}>
                        {isLate ? `${Math.abs(daysLeft)}g gecikti` : daysLeft === 0 ? "Bugün!" : `${daysLeft}g`}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-[11px] text-slate-400 text-center py-1">{tr("Önümüzdeki 7 günde termin yok")}</div>
            )}
          </div>

          {/* 5. CRM Takip Hatırlatmaları */}
          {(user?.role === "admin" || user?.role === "sales") && <CrmFollowupBox tr={tr} setTab={setTab} />}

          {/* 6. Durum Özeti */}
          <div className="zen-panel p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">{tr("Durum Özeti")}</div>
            <div className="space-y-2.5">
              {[
                { label: tr("Teklif"),          count: (summary.teklifler || []).length, dot: "bg-slate-400",   text: "text-slate-600 dark:text-slate-400" },
                { label: tr("Onay / İmalatta"), count: summary.imalatta?.length || 0,    dot: "bg-amber-500",   text: "text-amber-600 dark:text-amber-400" },
                { label: tr("Kargolanacak"),    count: summary.kargolanacak?.length || 0, dot: "bg-sky-500",     text: "text-sky-600 dark:text-sky-400" },
                { label: tr("Ödeme Bekliyor"),  count: summary.yollandi?.length || 0,     dot: "bg-orange-500",  text: "text-orange-600 dark:text-orange-400" },
                { label: tr("Ödendi"),          count: summary.odendi?.length || 0,       dot: "bg-emerald-500", text: "text-emerald-600 dark:text-emerald-400" },
              ].map(({ label, count, dot, text }) => (
                <div key={label} className="flex items-center justify-between text-[12px]">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                    <span className="text-slate-700 dark:text-slate-300">{label}</span>
                  </div>
                  <span className={`zen-mono font-bold text-sm ${text}`}>{count}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-200 dark:border-[#22304A] flex justify-between text-[11px]">
              <span className="text-slate-500 dark:text-slate-400">{tr("Toplam Aktif")}</span>
              <span className="zen-mono font-bold text-slate-800 dark:text-slate-100">
                {(summary.teklifler || []).length + (summary.imalatta?.length || 0) + (summary.kargolanacak?.length || 0) + (summary.yollandi?.length || 0)}
              </span>
            </div>
          </div>

          {/* 7. Son Siparişler */}
          <div className="zen-panel p-4">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-3">{tr("Son Siparişler")}</div>
            <div>
              {(summary.recentOrders || []).map((o) => {
                const sc = { teklif: "bg-slate-500/15 text-slate-500 dark:text-slate-400", onay_bekliyor: "bg-orange-500/15 text-orange-600 dark:text-orange-400", imalata_hazir: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", imalatta: "bg-amber-500/15 text-amber-600 dark:text-amber-400", kargolanacak: "bg-sky-500/15 text-sky-600 dark:text-sky-400", yollandi: "bg-orange-500/15 text-orange-600 dark:text-orange-400", odendi: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400", iptal: "bg-rose-500/15 text-rose-600 dark:text-rose-400" };
                const sl = lang === "en"
                  ? { teklif: "Quote", onay_bekliyor: "Pending", imalata_hazir: "Approved", imalatta: "In Prod.", kargolanacak: "Shipping", yollandi: "Shipped", odendi: "Paid", iptal: "Cancelled" }
                  : { teklif: "Teklif", onay_bekliyor: "Onay Bek.", imalata_hazir: "İmalata Hazır", imalatta: "İmalatta", kargolanacak: "Kargoda", yollandi: "Yollandı", odendi: "Ödendi", iptal: "İptal" };
                return (
                  <div key={o.id} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-[#22304A] last:border-0">
                    <div className="min-w-0">
                      <div className="zen-mono text-[11px] font-bold text-[#E5322A]">{ono(o)}</div>
                      <div className="text-[10px] text-slate-500 truncate max-w-[150px]">{o.customerName}</div>
                    </div>
                    <span className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${sc[o.status] || "bg-slate-500/15 text-slate-500"}`}>{sl[o.status] || o.status}</span>
                  </div>
                );
              })}
              {(summary.recentOrders || []).length === 0 && (
                <div className="text-[11px] text-slate-400 text-center py-4">{tr("Henüz sipariş yok")}</div>
              )}
            </div>
          </div>

        </div>
      </div>
    </aside>
  );
}
